<template>
  <div class="select-filters-container">
    <div
      class="d-flex justify-space-between align-center"
      :class="{ 'mb-4': hasSelectedFilters, 'mb-0': !hasSelectedFilters }"
    >
      <div
        class="filters-header text-secondary font-weight-bold"
        :class="{ 'mb-2': !mdAndUp }"
      >
        Filters
      </div>
      <a
        v-if="hasSelectedFilters"
        class="reset-link text-primary"
        @click="props.resetSelections"
        >Reset</a
      >
    </div>
    <div class="chips-outer-container">
      <transition
        name="fade-height"
        @before-leave="setInitialHeight"
        @leave="animateLeave"
      >
        <div
          v-if="hasSelectedFilters"
          class="chips-wrapper"
          :class="{ 'mb-4': !mdAndUp }"
        >
          <v-chip-group no-gutters column>
            <!-- Subject chips -->
            <v-chip
              v-for="subject in selectedSubjectsWithLabels"
              :key="subject.value"
              closable
              color="primary"
              class="custom-chip"
              close-icon="mdi-close"
              variant="flat"
              @click:close="reportsStore.setSubject(subject.value, false)"
            >
              {{ subject.label }}
            </v-chip>

            <!-- Format chips -->
            <v-chip
              v-for="format in selectedFormatsWithLabels"
              :key="format.value"
              closable
              color="primary"
              class="custom-chip"
              close-icon="mdi-close"
              variant="flat"
              @click:close="reportsStore.setFormat(format.value, false)"
            >
              {{ format.label }}
            </v-chip>

            <!-- Grade chips -->
            <v-chip
              v-for="grade in selectedGradesWithLabels"
              :key="grade.value"
              closable
              color="primary"
              class="custom-chip"
              close-icon="mdi-close"
              variant="flat"
              @click:close="reportsStore.setGrade(grade.value, false)"
            >
              {{ grade.label }}
            </v-chip>

            <!-- Standards chips -->
            <v-chip
              v-for="standard in reportsStore.selectedStandards"
              :key="`standard-${standard}`"
              closable
              color="primary"
              class="custom-chip"
              close-icon="mdi-close"
              variant="flat"
              size="x-small"
              @click:close="reportsStore.setStandard(standard, false)"
            >
              {{ standard }}
            </v-chip>
          </v-chip-group>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useReportsStore } from '../../store';
  import { useDisplay } from 'vuetify';
  import { computed, onMounted, onUnmounted, ref } from 'vue';

  const props = defineProps<{
    resetSelections: () => void;
    class?: string;
  }>();

  const reportsStore = useReportsStore();
  const display = useDisplay();
  const mdAndUp = computed(() => display.mdAndUp.value);
  const observer = ref<ResizeObserver | null>(null);

  const hasSelectedFilters = computed(
    () =>
      reportsStore.selectedSubjects.length > 0 ||
      reportsStore.selectedFormats.length > 0 ||
      reportsStore.selectedGrades.length > 0 ||
      reportsStore.selectedStandards.length > 0,
  );

  onMounted(async () => {
    await reportsStore.fetchFilters();

    observer.value = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const chip = entry.target as HTMLElement;
        const content = chip.querySelector('.v-chip__content') as HTMLElement;
        if (content) {
          const textSpan = content.firstElementChild as HTMLElement;
          if (textSpan) {
            const hasOverflow = textSpan.scrollWidth > content.clientWidth;
            chip.classList.toggle('has-overflow', hasOverflow);
          }
        }
      });
    });

    // Wait for chips to be rendered
    setTimeout(() => {
      document.querySelectorAll('.custom-chip').forEach((chip) => {
        if (observer.value) {
          observer.value.observe(chip);
        }
      });
    }, 100);
  });

  // Properly placed outside of onMounted
  onUnmounted(() => {
    if (observer.value) {
      observer.value.disconnect();
    }
  });

  // Animation helpers
  function setInitialHeight(element: Element) {
    const height = (element as HTMLElement).offsetHeight;
    (element as HTMLElement).style.height = height + 'px';
  }

  function animateLeave(element: Element, done: () => void) {
    requestAnimationFrame(() => {
      (element as HTMLElement).style.height = '0';
      (element as HTMLElement).style.opacity = '0';
      element.addEventListener('transitionend', done, { once: true });
    });
  }

  // Helper function to find the label for a filter value
  const getFilterLabel = (filterId: string, value: string): string => {
    const filter = reportsStore.filters.find((f) => f.id === filterId);
    if (!filter) return value;

    const option = filter.options.find((opt) => opt.value === value);
    return option ? option.label : value;
  };

  // Computed properties for selected filters with proper labels
  const selectedSubjectsWithLabels = computed(() =>
    reportsStore.selectedSubjects.map((subject) => ({
      value: subject,
      label: getFilterLabel('subject', subject),
    })),
  );

  const selectedFormatsWithLabels = computed(() =>
    reportsStore.selectedFormats.map((format) => ({
      value: format,
      label: getFilterLabel('format', format),
    })),
  );

  const selectedGradesWithLabels = computed(() =>
    reportsStore.selectedGrades.map((grade) => ({
      value: grade,
      label: getFilterLabel('grade', grade),
    })),
  );
</script>

<style lang="scss" scoped>
  .select-filters-container {
    overflow: hidden;
  }

  .chips-outer-container {
    position: relative;
  }

  .chips-wrapper {
    width: 100%;
  }

  .custom-chip {
    border-radius: 4px !important;
    height: 32px !important;
    font-size: 12px !important;
    background-color: rgb(var(--v-theme-primary)) !important;
    color: white !important;
    max-width: 200px !important;
    position: relative !important;
  }

  .custom-chip :deep(.v-chip__content) {
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    position: relative !important;
  }

  .custom-chip.has-overflow :deep(.v-chip__content) {
    padding-right: 32px !important;

    &::after {
      content: '' !important;
      position: absolute !important;
      right: 0 !important;
      top: 0 !important;
      height: 100% !important;
      width: 32px !important;
      background: linear-gradient(
        to right,
        transparent,
        rgb(var(--v-theme-primary))
      ) !important;
      pointer-events: none !important;
    }
  }

  .custom-chip :deep(.v-chip__close) {
    color: white !important;
    opacity: 1 !important;
    position: relative !important;
    z-index: 1 !important;
  }

  .reset-link {
    cursor: pointer;
    text-decoration: underline;
    margin-top: 0;
    display: inline-block;
  }

  // Fade and height transition
  .fade-height-enter-active,
  .fade-height-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
  }

  .fade-height-enter-from,
  .fade-height-leave-to {
    opacity: 0;
    height: 0;
  }
</style>
